import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Database,
  ChevronRight,
  ChevronLeft,
  Loader2,
  Table2,
  AlertTriangle,
  Download,
  Search,
  Save,
  RotateCcw,
  Check,
  Globe,
  Sparkles,
  Plus,
  FileJson,
  FileSpreadsheet,
  ExternalLink,
} from 'lucide-react';
import { api, handleApiError } from '@/services/client';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { toast } from 'sonner';

interface Session {
  id: string;
  name?: string;
}

interface TabItem {
  id: string;
  title: string;
  url: string;
  domain?: string;
  favicon?: string;
}

function tabFromApi(raw: Record<string, unknown>): TabItem {
  const title = raw.title;
  return {
    id: String(raw.id ?? ''),
    title: typeof title === 'string' ? title : '',
    url: String(raw.url ?? ''),
    domain: typeof raw.domain === 'string' ? raw.domain : undefined,
    favicon: typeof raw.favicon === 'string' ? raw.favicon : undefined,
  };
}

function analyzeTimeoutMs(tabCount: number) {
  return Math.min(300_000, 60_000 + tabCount * 45_000);
}

interface TabSourceRef {
  tab_id: string;
  url: string;
  title: string;
  favicon?: string;
}

interface ExtractedTable {
  index: number;
  headers: string[];
  rows: string[][];
}

interface DetectedTabSource {
  tab_id: string;
  url: string;
  title: string;
  favicon?: string;
  tables: ExtractedTable[];
  row_count: number;
  has_data: boolean;
  extract_error?: string;
}

interface SchemaColumn {
  id: string;
  name: string;
  type: string;
  included: boolean;
  is_join_key?: boolean;
  source?: string;
  map_from?: string[];
}

interface UnitWarning {
  column: string;
  units: string[];
  message: string;
}

interface FlaggedCell {
  row: number;
  column: string;
  value: string;
  reason: string;
  source?: string;
}

interface MergedResult {
  columns: string[];
  data: Record<string, unknown>[];
  total_count: number;
  issues?: string[];
  flagged_cells?: FlaggedCell[];
}

type WizardStep = 'select' | 'analyze' | 'schema' | 'results';

const STEPS: { id: WizardStep; label: string }[] = [
  { id: 'select', label: 'Select source tabs' },
  { id: 'analyze', label: 'Detect tables & lists' },
  { id: 'schema', label: 'Review unified schema' },
  { id: 'results', label: 'Query & export' },
];

function stepIndex(step: WizardStep) {
  return STEPS.findIndex((s) => s.id === step);
}

function escapeCsvCell(value: unknown): string {
  const s = value == null ? '' : String(value);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function downloadCsv(columns: string[], rows: Record<string, unknown>[], filename: string) {
  const header = columns.map(escapeCsvCell).join(',');
  const body = rows.map((row) => columns.map((col) => escapeCsvCell(row[col])).join(',')).join('\n');
  const blob = new Blob([`${header}\n${body}`], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function downloadJson(columns: string[], rows: Record<string, unknown>[], filename: string) {
  const blob = new Blob([JSON.stringify({ columns, rows }, null, 2)], {
    type: 'application/json;charset=utf-8',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function downloadExcel(columns: string[], rows: Record<string, unknown>[], filename: string) {
  const header = columns.map((c) => `<th>${c.replace(/</g, '&lt;')}</th>`).join('');
  const body = rows
    .map(
      (row) =>
        `<tr>${columns.map((col) => `<td>${String(row[col] ?? '').replace(/</g, '&lt;')}</td>`).join('')}</tr>`,
    )
    .join('');
  const html = `<html><head><meta charset="utf-8"></head><body><table><thead><tr>${header}</tr></thead><tbody>${body}</tbody></table></body></html>`;
  const blob = new Blob([html], { type: 'application/vnd.ms-excel;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename.endsWith('.xls') ? filename : `${filename}.xls`;
  a.click();
  URL.revokeObjectURL(url);
}

export function DataAggregationPage() {
  const [step, setStep] = useState<WizardStep>('select');
  const [sessions, setSessions] = useState<Session[]>([]);
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);
  const [tabs, setTabs] = useState<TabItem[]>([]);
  const [selectedTabIds, setSelectedTabIds] = useState<Set<string>>(new Set());
  const [isLoadingTabs, setIsLoadingTabs] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isMerging, setIsMerging] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [analyzeError, setAnalyzeError] = useState<string | null>(null);
  const [detected, setDetected] = useState<DetectedTabSource[]>([]);
  const [schema, setSchema] = useState<SchemaColumn[]>([]);
  const [schemaNotes, setSchemaNotes] = useState('');
  const [joinKey, setJoinKey] = useState('');
  const [unitWarnings, setUnitWarnings] = useState<UnitWarning[]>([]);
  const [merged, setMerged] = useState<MergedResult | null>(null);
  const [savedAppId, setSavedAppId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortCol, setSortCol] = useState<string | null>(null);
  const [sortAsc, setSortAsc] = useState(true);
  const [projectName, setProjectName] = useState('Aggregated dataset');
  const [projectDescription, setProjectDescription] = useState('');

  useEffect(() => {
    void loadSessions();
  }, []);

  useEffect(() => {
    if (selectedSessionId) void loadTabs(selectedSessionId);
  }, [selectedSessionId]);

  /**
   * If the user navigated here from Tab Monitor (bulk action or cluster
   * action) with a pre-selected set of tab ids stashed in sessionStorage,
   * restore them once tabs have loaded. The stash is cleared so a refresh
   * doesn't keep re-applying the selection.
   */
  useEffect(() => {
    if (tabs.length === 0) return;
    let stashed: string[] | null = null;
    try {
      const raw = sessionStorage.getItem('khiimori.dataAggregation.tabIds');
      if (raw) {
        stashed = JSON.parse(raw);
        sessionStorage.removeItem('khiimori.dataAggregation.tabIds');
      }
    } catch {
      // ignore — fall through to default behavior
    }
    if (!stashed || stashed.length === 0) return;
    const present = new Set(tabs.map((t) => t.id));
    const valid = stashed.filter((id) => present.has(id));
    if (valid.length > 0) {
      setSelectedTabIds(new Set(valid));
      toast.success(`${valid.length} tab${valid.length > 1 ? 's' : ''} pre-selected from Tab Monitor`);
    }
  }, [tabs]);

  const loadSessions = async () => {
    try {
      const res = await api.get('/sessions');
      const list: Session[] = res.data?.sessions || [];
      setSessions(list);
      if (list.length > 0) setSelectedSessionId(list[0].id);
    } catch {
      toast.error('Failed to load sessions');
    }
  };

  const loadTabs = async (sessionId: string) => {
    setIsLoadingTabs(true);
    setSelectedTabIds(new Set());
    try {
      const res = await api.get(`/sessions/${sessionId}/tabs`);
      const rawTabs = (res.data?.tabs || []) as Record<string, unknown>[];
      setTabs(rawTabs.map(tabFromApi).filter((t) => t.id && t.url));
    } catch {
      setTabs([]);
      toast.error('Failed to load tabs');
    } finally {
      setIsLoadingTabs(false);
    }
  };

  const toggleTab = (tabId: string) => {
    setSelectedTabIds((prev) => {
      const next = new Set(prev);
      if (next.has(tabId)) next.delete(tabId);
      else next.add(tabId);
      return next;
    });
  };

  const selectedTabRefs = useCallback((): TabSourceRef[] => {
    return tabs
      .filter((t) => selectedTabIds.has(t.id))
      .map((t) => ({
        tab_id: t.id,
        url: t.url,
        title: t.title,
        favicon: t.favicon,
      }));
  }, [tabs, selectedTabIds]);

  const runAnalyze = async () => {
    const refs = selectedTabRefs();
    if (refs.length < 1) {
      toast.error('Select at least one tab');
      return;
    }
    setIsAnalyzing(true);
    setAnalyzeError(null);
    try {
      const res = await api.post(
        '/data-aggregation/analyze',
        { tabs: refs },
        { timeout: analyzeTimeoutMs(refs.length) },
      );
      const nextDetected = (res.data?.detected || []) as DetectedTabSource[];
      const nextSchema = (res.data?.schema || []) as SchemaColumn[];
      if (nextDetected.length === 0) {
        setAnalyzeError('No data could be extracted from the selected tabs.');
        return;
      }
      setDetected(nextDetected);
      setSchema(nextSchema);
      setSchemaNotes(String(res.data?.schema_notes || ''));
      setJoinKey(String(res.data?.join_key || ''));
      setUnitWarnings((res.data?.unit_warnings || []) as UnitWarning[]);
      setMerged(null);
      setSavedAppId(null);
      setStep('analyze');
    } catch (err: unknown) {
      const msg = handleApiError(err);
      setAnalyzeError(msg);
      toast.error(msg);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const runMerge = async () => {
    if (detected.length === 0 || schema.length === 0) {
      toast.error('Run analysis first');
      return;
    }
    setIsMerging(true);
    try {
      const res = await api.post(
        '/data-aggregation/merge',
        { detected, schema },
        { timeout: 60_000 },
      );
      setMerged({
        columns: res.data?.columns || [],
        data: res.data?.data || [],
        total_count: res.data?.total_count ?? 0,
        issues: res.data?.issues,
        flagged_cells: res.data?.flagged_cells,
      });
      setStep('results');
    } catch (err: unknown) {
      const msg = handleApiError(err);
      toast.error(msg);
    } finally {
      setIsMerging(false);
    }
  };

  const saveAsApp = async () => {
    if (!merged || merged.data.length === 0) {
      toast.error('Merge a dataset first');
      return;
    }
    if (!selectedSessionId) {
      toast.error('No session selected');
      return;
    }
    setIsSaving(true);
    try {
      const res = await api.post('/data-aggregation/save-app', {
        session_id: selectedSessionId,
        name: projectName.trim() || 'Aggregated dataset',
        description: projectDescription.trim(),
        tab_sources: selectedTabRefs(),
        columns: merged.columns,
        rows: merged.data,
        issues: merged.issues,
        flagged_cells: merged.flagged_cells,
      });
      const appId = res.data?.app_id as string;
      setSavedAppId(appId);
      toast.success('Saved as generated app');
    } catch (err: unknown) {
      toast.error(handleApiError(err));
    } finally {
      setIsSaving(false);
    }
  };

  const addSchemaColumn = () => {
    setSchema((cols) => [
      ...cols,
      {
        id: crypto.randomUUID(),
        name: `Column ${cols.length + 1}`,
        type: 'text',
        included: true,
        map_from: [],
      },
    ]);
  };

  const updateColumn = (id: string, patch: Partial<SchemaColumn>) => {
    setSchema((cols) => cols.map((c) => (c.id === id ? { ...c, ...patch } : c)));
  };

  const filteredRows = useMemo(() => {
    if (!merged) return [];
    let rows = [...merged.data];
    const q = searchQuery.trim().toLowerCase();
    if (q) {
      rows = rows.filter((row) =>
        Object.values(row).some((v) => String(v ?? '').toLowerCase().includes(q)),
      );
    }
    if (sortCol) {
      rows.sort((a, b) => {
        const av = String(a[sortCol] ?? '');
        const bv = String(b[sortCol] ?? '');
        const cmp = av.localeCompare(bv, undefined, { numeric: true, sensitivity: 'base' });
        return sortAsc ? cmp : -cmp;
      });
    }
    return rows;
  }, [merged, searchQuery, sortCol, sortAsc]);

  const toggleSort = (col: string) => {
    if (sortCol === col) setSortAsc((v) => !v);
    else {
      setSortCol(col);
      setSortAsc(true);
    }
  };

  const resetWizard = () => {
    setStep('select');
    setDetected([]);
    setSchema([]);
    setSchemaNotes('');
    setJoinKey('');
    setUnitWarnings([]);
    setMerged(null);
    setSavedAppId(null);
    setSearchQuery('');
    setSortCol(null);
    setAnalyzeError(null);
  };

  const currentStepIdx = stepIndex(step);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <Database className="h-5 w-5 text-primary" />
            <h2 className="text-lg font-semibold text-foreground">Data Aggregation</h2>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            Combine structured data — tables and lists — from multiple tabs into one queryable dataset.
            The system detects source schemas, reconciles them with AI, and merges rows for export or app generation.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={resetWizard}>
          <RotateCcw className="mr-1.5 h-4 w-4" />
          Start over
        </Button>
      </div>

      <div className="flex flex-wrap gap-2">
        {STEPS.map((s, i) => (
          <div key={s.id} className="flex items-center gap-2">
            <Badge
              variant={i <= currentStepIdx ? 'default' : 'secondary'}
              className="cursor-default"
            >
              {i < currentStepIdx ? <Check className="mr-1 h-3 w-3" /> : null}
              {i + 1}. {s.label}
            </Badge>
            {i < STEPS.length - 1 && <ChevronRight className="h-4 w-4 text-muted-foreground" />}
          </div>
        ))}
      </div>

      {isAnalyzing && (
        <Card className="p-8 flex flex-col items-center justify-center gap-3 text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm font-medium text-foreground">Detecting tables and lists…</p>
          <p className="text-xs text-muted-foreground max-w-md">
            Fetching each tab and parsing structured data. This can take up to a minute per tab.
          </p>
        </Card>
      )}

      {!isAnalyzing && step === 'select' && (
        <Card className="p-5 space-y-4">
          {analyzeError && (
            <div className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
              <span>{analyzeError}</span>
            </div>
          )}
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <label className="text-sm font-medium text-foreground">Session</label>
            <select
              className="rounded-md border border-border bg-background px-3 py-2 text-sm"
              value={selectedSessionId ?? ''}
              onChange={(e) => setSelectedSessionId(e.target.value)}
            >
              {sessions.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name || s.id.slice(0, 8)}
                </option>
              ))}
            </select>
            <span className="text-sm text-muted-foreground">
              {selectedTabIds.size} tab{selectedTabIds.size === 1 ? '' : 's'} selected
            </span>
          </div>

          {isLoadingTabs ? (
            <div className="flex items-center justify-center py-12 text-muted-foreground">
              <Loader2 className="mr-2 h-5 w-5 animate-spin" />
              Loading tabs…
            </div>
          ) : tabs.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              No tabs in this session. Open pages with tables or lists in your browser extension first.
            </p>
          ) : (
            <div className="grid gap-2 max-h-80 overflow-y-auto">
              {tabs.map((tab) => {
                const selected = selectedTabIds.has(tab.id);
                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => toggleTab(tab.id)}
                    className={`flex items-start gap-3 rounded-lg border p-3 text-left transition-colors ${
                      selected
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:border-primary/40'
                    }`}
                  >
                    <div
                      className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded border ${
                        selected ? 'border-primary bg-primary text-primary-foreground' : 'border-border'
                      }`}
                    >
                      {selected ? <Check className="h-3 w-3" /> : null}
                    </div>
                    {tab.favicon ? (
                      <img src={tab.favicon} alt="" className="h-4 w-4 mt-0.5 shrink-0" />
                    ) : (
                      <Globe className="h-4 w-4 mt-0.5 shrink-0 text-muted-foreground" />
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium">{tab.title || tab.url}</p>
                      <p className="truncate text-xs text-muted-foreground">{tab.url}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          <div className="flex justify-end">
            <Button onClick={runAnalyze} disabled={selectedTabIds.size === 0 || isAnalyzing}>
              {isAnalyzing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Detecting tables…
                </>
              ) : (
                <>
                  Detect data
                  <ChevronRight className="ml-1 h-4 w-4" />
                </>
              )}
            </Button>
          </div>
        </Card>
      )}

      {step === 'analyze' && !isAnalyzing && (
        <Card className="p-5 space-y-4">
          <p className="text-sm text-muted-foreground">
            Structured data detected in each source tab. Review before defining the unified schema.
          </p>
          {detected.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">
              Nothing detected. Go back and try different tabs.
            </p>
          ) : (
          <div className="space-y-4">
            {detected.map((src) => (
              <div key={src.tab_id} className="rounded-lg border border-border p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-medium truncate">{src.title}</p>
                    <a
                      href={src.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs text-primary hover:underline truncate max-w-full"
                    >
                      {src.url}
                      <ExternalLink className="h-3 w-3 shrink-0" />
                    </a>
                  </div>
                  <Badge variant={src.has_data ? 'default' : 'secondary'}>
                    {src.row_count} row{src.row_count === 1 ? '' : 's'}
                  </Badge>
                </div>
                {src.extract_error && (
                  <p className="mt-2 flex items-center gap-1 text-xs text-amber-600">
                    <AlertTriangle className="h-3.5 w-3.5" />
                    {src.extract_error} — using fallback metadata row
                  </p>
                )}
                {src.tables.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {src.tables.slice(0, 2).map((table) => (
                      <div key={table.index} className="overflow-x-auto rounded border border-border/60">
                        <table className="min-w-full text-xs">
                          <thead>
                            <tr className="bg-muted/50">
                              {table.headers.map((h, i) => (
                                <th key={i} className="px-2 py-1 text-left font-medium">
                                  {h}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {table.rows.slice(0, 3).map((row, ri) => (
                              <tr key={ri} className="border-t border-border/40">
                                {row.map((cell, ci) => (
                                  <td key={ci} className="px-2 py-1 max-w-[12rem] truncate">
                                    {cell}
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {table.rows.length > 3 && (
                          <p className="px-2 py-1 text-xs text-muted-foreground">
                            +{table.rows.length - 3} more rows
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
          )}
          <div className="flex justify-between">
            <Button variant="outline" onClick={() => setStep('select')}>
              <ChevronLeft className="mr-1 h-4 w-4" />
              Back
            </Button>
            <Button onClick={() => setStep('schema')}>
              Review schema
              <ChevronRight className="ml-1 h-4 w-4" />
            </Button>
          </div>
        </Card>
      )}

      {step === 'schema' && (
        <Card className="p-5 space-y-4">
          <p className="text-sm text-muted-foreground">
            AI-proposed unified schema. Rows sharing the same{' '}
            <strong>{joinKey || schema.find((c) => c.is_join_key)?.name || 'join key'}</strong> across tabs
            are merged into one row. Rename, retype, exclude, or add columns before merging.
          </p>
          {schemaNotes && (
            <div className="flex items-start gap-2 rounded-lg border border-primary/20 bg-primary/5 p-3 text-sm">
              <Sparkles className="h-4 w-4 shrink-0 mt-0.5 text-primary" />
              <span>{schemaNotes}</span>
            </div>
          )}
          {unitWarnings.length > 0 && (
            <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 text-sm text-amber-800 space-y-1">
              <p className="font-medium">Inconsistent units detected — pick canonical types before export:</p>
              {unitWarnings.map((w, i) => (
                <p key={i}>
                  <strong>{w.column}</strong>: {w.message || w.units.join(', ')}
                </p>
              ))}
            </div>
          )}
          <div className="flex justify-end">
            <Button variant="outline" size="sm" onClick={addSchemaColumn}>
              <Plus className="mr-1.5 h-4 w-4" />
              Add column
            </Button>
          </div>
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-muted/50 border-b border-border">
                  <th className="px-3 py-2 text-left">Include</th>
                  <th className="px-3 py-2 text-left">Column</th>
                  <th className="px-3 py-2 text-left">Type</th>
                  <th className="px-3 py-2 text-left">Maps from</th>
                </tr>
              </thead>
              <tbody>
                {schema.map((col) => (
                  <tr key={col.id} className="border-b border-border/50">
                    <td className="px-3 py-2">
                      <input
                        type="checkbox"
                        checked={col.included}
                        onChange={(e) => updateColumn(col.id, { included: e.target.checked })}
                      />
                    </td>
                    <td className="px-3 py-2">
                      <input
                        className="w-full rounded border border-border bg-background px-2 py-1 text-sm"
                        value={col.name}
                        onChange={(e) => updateColumn(col.id, { name: e.target.value })}
                      />
                    </td>
                    <td className="px-3 py-2">
                      <select
                        className="rounded border border-border bg-background px-2 py-1 text-sm"
                        value={col.type}
                        onChange={(e) => updateColumn(col.id, { type: e.target.value })}
                      >
                        <option value="text">text</option>
                        <option value="number">number</option>
                        <option value="date">date</option>
                        <option value="url">url</option>
                      </select>
                    </td>
                    <td className="px-3 py-2 text-muted-foreground text-xs">
                      {(col.map_from && col.map_from.length > 0 ? col.map_from : col.source ? [col.source] : [])
                        .join(', ') || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex justify-between">
            <Button variant="outline" onClick={() => setStep('analyze')}>
              <ChevronLeft className="mr-1 h-4 w-4" />
              Back
            </Button>
            <Button onClick={runMerge} disabled={isMerging}>
              {isMerging ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Merging…
                </>
              ) : (
                <>
                  <Table2 className="mr-2 h-4 w-4" />
                  Merge dataset
                </>
              )}
            </Button>
          </div>
        </Card>
      )}

      {step === 'results' && merged && (
        <Card className="p-5 space-y-4">
          {merged.issues && merged.issues.length > 0 && (
            <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 text-sm text-amber-700">
              {merged.issues.map((issue, i) => (
                <p key={i}>{issue}</p>
              ))}
            </div>
          )}

          {(merged.flagged_cells?.length ?? 0) > 0 && (
            <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 text-sm">
              <p className="font-medium text-amber-800 mb-2">Review flagged cells before export</p>
              <ul className="space-y-1 text-amber-700 max-h-32 overflow-y-auto">
                {merged.flagged_cells!.map((f, i) => (
                  <li key={i}>
                    Row {f.row + 1}, <strong>{f.column}</strong>: {f.value} — {f.reason}
                    {f.source ? ` (${f.source})` : ''}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <input
                className="w-full rounded-md border border-border bg-background py-2 pl-9 pr-3 text-sm"
                placeholder="Search rows…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <div className="flex flex-wrap gap-2 items-center">
              <input
                className="rounded-md border border-border bg-background px-3 py-2 text-sm"
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
                placeholder="Dataset name"
              />
              <input
                className="rounded-md border border-border bg-background px-3 py-2 text-sm min-w-[12rem]"
                value={projectDescription}
                onChange={(e) => setProjectDescription(e.target.value)}
                placeholder="Description (optional)"
              />
              <Button variant="outline" size="sm" onClick={saveAsApp} disabled={isSaving}>
                {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                <span className="ml-1.5">Save as app</span>
              </Button>
              {savedAppId && (
                <Link
                  to={`/apps/${savedAppId}`}
                  className="text-sm text-primary hover:underline inline-flex items-center gap-1"
                >
                  View app <ExternalLink className="h-3.5 w-3.5" />
                </Link>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  downloadCsv(merged.columns, filteredRows, `${projectName || 'dataset'}.csv`)
                }
              >
                <Download className="mr-1.5 h-4 w-4" />
                CSV
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  downloadExcel(merged.columns, filteredRows, `${projectName || 'dataset'}`)
                }
              >
                <FileSpreadsheet className="mr-1.5 h-4 w-4" />
                Excel
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  downloadJson(merged.columns, filteredRows, `${projectName || 'dataset'}.json`)
                }
              >
                <FileJson className="mr-1.5 h-4 w-4" />
                JSON
              </Button>
            </div>
          </div>

          <p className="text-sm text-muted-foreground">
            {filteredRows.length} of {merged.total_count} rows
          </p>

          <div className="overflow-x-auto rounded-lg border border-border max-h-[28rem] overflow-y-auto">
            <table className="min-w-full text-sm">
              <thead className="sticky top-0 bg-muted/80 backdrop-blur">
                <tr>
                  {merged.columns.map((col) => (
                    <th
                      key={col}
                      className="cursor-pointer px-3 py-2 text-left font-medium whitespace-nowrap"
                      onClick={() => toggleSort(col)}
                    >
                      {col}
                      {sortCol === col ? (sortAsc ? ' ↑' : ' ↓') : ''}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((row, i) => (
                  <tr key={i} className="border-t border-border/50 hover:bg-muted/30">
                    {merged.columns.map((col) => (
                      <td key={col} className="px-3 py-2 max-w-xs truncate">
                        {String(row[col] ?? '')}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex justify-between">
            <Button variant="outline" onClick={() => setStep('schema')}>
              <ChevronLeft className="mr-1 h-4 w-4" />
              Edit schema
            </Button>
            <Button variant="outline" onClick={() => setStep('select')}>
              Add more tabs
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}

export default DataAggregationPage;
